/**
 * pages/signup/steps/Terminal.tsx — Pending, Documents, Rejected, Blocked.
 *
 * Four terminal-ish signup screens grouped in one file because each
 * is essentially a static message + one call-to-action.
 *
 * COPY RULES (see shared/onboarding::REASON_LEAK_WORDS):
 *   * `rejected` copy is deliberately vague — no field-level hint of
 *     what tripped verification. Stating a reason gives a fraudster
 *     a tuning signal.
 *   * `blocked` copy is warm but final — the user cannot progress
 *     from here today. Common cases: under-18, sanctions match. We
 *     don't name which.
 *   * `pending` copy is optimistic — we're on it, we'll be in touch.
 *   * `documents` copy is actionable — we need something from you.
 *     For now we route to a support email; document upload lands
 *     with the sponsor bank's vendor pick.
 *
 * Blocked screen wipes persisted progress on mount — the user's
 * abandonment of this session should NOT resume back into a flow
 * that can't proceed.
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { clearProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";
import styles from "./Terminal.module.css";

// ---------------------------------------------------------------------
// Pending — vendor said "we'll look at this and get back to you".
// ---------------------------------------------------------------------

export function SignupPending() {
  return (
    <SignupShell
      title="We're reviewing your information"
      subtitle="This can take up to a few business days. You'll hear from us by SMS the moment there's an update."
    >
      <div className={styles.center}>
        <p className={styles.body}>
          You can close this page. When your account is ready, we'll
          text you a link to sign in.
        </p>
        <p className={styles.contactSmall}>
          Questions?{" "}
          <a href="mailto:support@astercash.com" className={styles.link}>
            support@astercash.com
          </a>
        </p>
      </div>
    </SignupShell>
  );
}

// ---------------------------------------------------------------------
// Documents — action_needed. Vendor wants supporting documents.
// Real document upload lands with the sponsor-bank vendor pick;
// today we route to support with a case reference we'd like the
// server to mint (unimplemented — a support email is the interim).
// ---------------------------------------------------------------------

export function SignupDocuments() {
  return (
    <SignupShell
      title="We need one more thing"
      subtitle="To finish verification we need a document from you — a photo ID or a recent utility bill in your name, usually."
    >
      <div className={styles.center}>
        <p className={styles.body}>
          Our team will guide you through what's needed. Email us and
          we'll take care of it — usually within a business day.
        </p>
        <a
          href="mailto:support@astercash.com?subject=Additional%20verification"
          className={styles.buttonLink}
        >
          Contact support
        </a>
      </div>
    </SignupShell>
  );
}

// ---------------------------------------------------------------------
// Rejected — vendor said no. DELIBERATELY VAGUE copy.
// ---------------------------------------------------------------------

export function SignupRejected() {
  return (
    <SignupShell
      title="We couldn't open your account"
      subtitle="We couldn't verify your identity with the information provided. Our support team can help — get in touch."
    >
      <div className={styles.center}>
        <a
          href="mailto:support@astercash.com?subject=Account%20verification%20help"
          className={styles.buttonLink}
        >
          Contact support
        </a>
        <p className={styles.contactSmall}>
          <Link to="/signin" className={styles.link}>
            Back to sign in
          </Link>
        </p>
      </div>
    </SignupShell>
  );
}

// ---------------------------------------------------------------------
// Blocked — terminal. Wipes persisted progress on mount.
//
// Common triggers: under-18, sanctions match, watchlist. We don't say
// which — the copy is warm and final without revealing anything.
// ---------------------------------------------------------------------

export function SignupBlocked() {
  useEffect(() => {
    // Terminal state — clear any persisted onboarding progress so the
    // user isn't routed back here on their next visit. The server-side
    // audit trail persists; the client-side breadcrumbs don't need to.
    clearProgress();
  }, []);

  return (
    <SignupShell
      title="We can't open an account for you"
      subtitle="Based on the information provided, we're not able to open an AsterCash account at this time."
    >
      <div className={styles.center}>
        <p className={styles.body}>
          If you believe this is a mistake, our team will look into it
          for you. Otherwise, thanks for your interest in AsterCash.
        </p>
        <a
          href="mailto:support@astercash.com?subject=Account%20eligibility"
          className={styles.buttonLink}
        >
          Contact support
        </a>
      </div>
    </SignupShell>
  );
}
